import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import {
  LeadsFilterField,
  LeadsGhostButton,
  LeadsPrimaryButton,
  leadsInputClass,
} from "./websiteLeadsShared";

function LeadsModalBackdrop({ onClose }) {
  return (
    <div
      className="website-leads-modal-backdrop fade show"
      onClick={onClose}
      aria-hidden="true"
    />
  );
}

function LeadsModalCloseButton({ onClick, disabled = false, label = "Close" }) {
  return (
    <button
      type="button"
      className="website-leads-modal__close"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <FaTimes size={14} />
    </button>
  );
}

function LeadsModalHeader({ title, subtitle, onClose, disabledClose = false, titleId }) {
  return (
    <div className="website-leads-modal__header">
      <div className="website-leads-modal__header-text">
        <h5 className="website-leads-modal__title" id={titleId}>
          {title}
        </h5>
        {subtitle && <p className="website-leads-modal__subtitle">{subtitle}</p>}
      </div>
      <LeadsModalCloseButton onClick={onClose} disabled={disabledClose} />
    </div>
  );
}

export function LeadsColumnSettingsModal({
  show,
  onClose,
  columns,
  visibility,
  onToggle,
  onReset,
}) {
  if (!show) return null;

  return (
    <>
      <LeadsModalBackdrop onClose={onClose} />
      <div
        className="modal fade show d-block website-leads-modal-wrap"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="website-leads-columns-title"
      >
        <div className="modal-dialog modal-dialog-centered website-leads-modal-dialog website-leads-modal-dialog--sm">
          <div className="modal-content website-leads-modal">
            <LeadsModalHeader
              titleId="website-leads-columns-title"
              title="Table Columns"
              subtitle="Choose which columns to show in the table."
              onClose={onClose}
            />

            <div className="website-leads-modal__body">
              <div className="website-leads-column-settings-grid">
                {columns.map((column) => {
                  const checked = visibility[column.key] !== false;
                  return (
                    <label
                      key={column.key}
                      className={`website-leads-column-settings-item${
                        checked ? " is-checked" : ""
                      }`}
                      htmlFor={`wl-col-${column.key}`}
                    >
                      <input
                        id={`wl-col-${column.key}`}
                        type="checkbox"
                        className="website-leads-column-settings-checkbox"
                        checked={checked}
                        onChange={() => onToggle(column.key)}
                      />
                      <span className="website-leads-column-settings-label">
                        {column.title}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="website-leads-modal__footer">
              <LeadsGhostButton onClick={onReset}>Reset defaults</LeadsGhostButton>
              <LeadsPrimaryButton onClick={onClose}>Done</LeadsPrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const EDIT_SECTIONS = [
  {
    title: "Contact Information",
    fields: [
      { key: "name", label: "Name", type: "text", placeholder: "Full name" },
      { key: "email", label: "Email", type: "email", placeholder: "Email address" },
      { key: "phone", label: "Contact", type: "text", placeholder: "Phone number" },
    ],
  },
  {
    title: "Lead Details",
    fields: [
      { key: "country", label: "Country", type: "text", placeholder: "Origin country" },
      {
        key: "destination",
        label: "Destination",
        type: "text",
        placeholder: "Destination country",
      },
      {
        key: "inquiry",
        label: "Inquiry",
        type: "textarea",
        placeholder: "Lead inquiry message…",
        fullWidth: true,
        rows: 3,
      },
    ],
  },
  {
    title: "Internal Notes",
    fields: [
      {
        key: "notes",
        label: "Notes",
        type: "textarea",
        placeholder: "Add internal notes for this lead…",
        fullWidth: true,
        rows: 3,
      },
    ],
  },
];

export function buildLeadEditDraft(item) {
  if (!item) return null;
  return {
    name: item.name || "",
    email: item.email || "",
    phone: item.phone || "",
    country: item.country || "",
    destination: item.destination || "",
    inquiry: item.inquiry || "",
    notes: item.notes || "",
  };
}

function LeadsEditField({ field, value, onChange }) {
  const inputId = `edit-${field.key}`;

  return (
    <LeadsFilterField
      label={field.label}
      id={inputId}
      className={field.fullWidth ? "website-leads-edit-field--full" : ""}
    >
      {field.type === "textarea" ? (
        <textarea
          id={inputId}
          className={`${leadsInputClass} website-leads-edit-textarea`}
          rows={field.rows || 2}
          placeholder={field.placeholder}
          value={value}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      ) : (
        <input
          id={inputId}
          type={field.type}
          className={leadsInputClass}
          placeholder={field.placeholder}
          value={value}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      )}
    </LeadsFilterField>
  );
}

export function LeadsEditLeadModal({ show, lead, saving, onClose, onSave }) {
  const [draft, setDraft] = useState(() => buildLeadEditDraft(lead));

  useEffect(() => {
    if (show && lead) {
      setDraft(buildLeadEditDraft(lead));
    }
  }, [show, lead]);

  if (!show || !lead) return null;

  const handleChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave(lead.id, draft);
  };

  return (
    <>
      <LeadsModalBackdrop onClose={saving ? undefined : onClose} />
      <div
        className="modal fade show d-block website-leads-modal-wrap"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="website-leads-edit-title"
      >
        <div className="modal-dialog modal-dialog-centered website-leads-modal-dialog website-leads-modal-dialog--lg">
          <div className="modal-content website-leads-modal website-leads-modal--edit">
            <form onSubmit={handleSubmit}>
              <LeadsModalHeader
                titleId="website-leads-edit-title"
                title="Edit Lead"
                subtitle="Update lead details and save your changes."
                onClose={onClose}
                disabledClose={saving}
              />

              <div className="website-leads-modal__body">
                {EDIT_SECTIONS.map((section) => (
                  <section key={section.title} className="website-leads-edit-section">
                    <h6 className="website-leads-edit-section__title">{section.title}</h6>
                    <div className="website-leads-edit-grid">
                      {section.fields.map((field) => (
                        <LeadsEditField
                          key={field.key}
                          field={field}
                          value={draft?.[field.key] ?? ""}
                          onChange={handleChange}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="website-leads-modal__footer">
                <LeadsGhostButton type="button" onClick={onClose} disabled={saving}>
                  Cancel
                </LeadsGhostButton>
                <LeadsPrimaryButton type="submit" disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </LeadsPrimaryButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
